import { Info } from "@mui/icons-material";
import { Box, Button, Dialog, Typography, useTheme } from "@mui/material";
import {
  useLicensingServiceV1,
  useUserManagementServiceV1,
} from "@rw1/api-services";
import { UserManagement_V1 } from "@rw1/auto-generated-clients";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef } from "react";
import { Trans } from "react-i18next";
import { ALL_RELEASE_CHANNELS_QUERY_KEY } from "../hooks/useAllExperiences";
import {
  LICENSE_ALLOCATIONS_QUERY_KEY,
  LICENSE_GROUP_ALLOCATIONS_QUERY_KEY,
} from "../hooks/useExternalExperiences";
import { useLocalization } from "../hooks/useLocalization";
import { GROUPS_FOR_RELEASE_CHANNEL_QUERY_KEY } from "../pages/experiences/hooks/useExperienceGroups";
import { INCOMING_LICENSES_QUERY_KEY } from "../pages/experiences/hooks/useIncomingLicenses";
import { USER_GROUPS_FOR_RELEASE_CHANNEL_QUERY_KEY } from "../pages/experiences/hooks/useUserGroupsForReleaseChannel";
import { ALL_LICENSES_QUERY_KEY } from "../pages/licenses/hooks/useExternalLicenses";

type GroupAccessData = UserManagement_V1.ReleaseChannel;
type TableAccessData = UserManagement_V1.UserGroupAccessData;

interface Props {
  open: boolean;
  onClose: () => void;
  groupName: string;
  experienceName: string;
  releaseChannelId: string;
  userGroupId: string;
  isExternal?: boolean;
  licenseId?: string;
}

export const RemoveGroupAccessDialog: React.FC<Props> = ({
  open,
  onClose,
  groupName,
  experienceName,
  releaseChannelId,
  userGroupId,
  isExternal = false,
  licenseId,
}) => {
  const { t } = useLocalization();
  const theme = useTheme();

  const queryClient = useQueryClient();
  const userManagementService = useUserManagementServiceV1();
  const licensingService = useLicensingServiceV1();

  // Preserve values during close transition
  const groupNameRef = useRef(groupName);
  const userGroupIdRef = useRef(userGroupId);

  useEffect(() => {
    if (open && groupName) {
      groupNameRef.current = groupName;
    }
  }, [open, groupName]);

  useEffect(() => {
    if (open && userGroupId) {
      userGroupIdRef.current = userGroupId;
    }
  }, [open, userGroupId]);

  const displayGroupName = open ? groupName : groupNameRef.current;
  const displayUserGroupId = open ? userGroupId : userGroupIdRef.current;

  // --- Helper for Optimistic Updates ---
  const updateCacheOptimistically = () => {
    const dialogKeys = [
      [USER_GROUPS_FOR_RELEASE_CHANNEL_QUERY_KEY, releaseChannelId],
    ];

    dialogKeys.forEach((key) => {
      queryClient.setQueryData<GroupAccessData[]>(key, (oldData) => {
        if (!oldData) return oldData;

        return oldData.filter(
          (item) => item.userGroupId !== displayUserGroupId,
        );
      });
    });

    const tableKeys = [
      [GROUPS_FOR_RELEASE_CHANNEL_QUERY_KEY, releaseChannelId],
    ];

    tableKeys.forEach((key) => {
      queryClient.setQueryData<TableAccessData[]>(key, (oldData) => {
        if (!oldData) return oldData;

        return oldData.filter(
          (item) => item.userGroupId !== displayUserGroupId,
        );
      });
    });
  };

  // Helper to invalidate relevant lists
  const invalidateGroupLists = () => {
    void queryClient.invalidateQueries({
      queryKey: [USER_GROUPS_FOR_RELEASE_CHANNEL_QUERY_KEY, releaseChannelId],
    });
    void queryClient.invalidateQueries({
      queryKey: [GROUPS_FOR_RELEASE_CHANNEL_QUERY_KEY, releaseChannelId],
    });
  };

  const mutation = useMutation({
    mutationFn: () =>
      userManagementService.removeReleaseChannelFromUserGroup(
        displayUserGroupId,
        releaseChannelId,
      ),
    onSuccess: () => {
      // Optimistic Update (Instant Removal)
      updateCacheOptimistically();

      // Double Invalidation (Consistency Check)
      invalidateGroupLists();
      setTimeout(invalidateGroupLists, 1000);

      void queryClient.invalidateQueries({
        queryKey: [ALL_RELEASE_CHANNELS_QUERY_KEY],
      });

      onClose();
    },
  });

  const externalMutation = useMutation({
    mutationFn: () =>
      licensingService.deallocateLicenseGroupSeat(
        licenseId!,
        displayUserGroupId,
      ),
    onSuccess: () => {
      // Optimistic Update
      updateCacheOptimistically();

      void queryClient.invalidateQueries({
        queryKey: [LICENSE_GROUP_ALLOCATIONS_QUERY_KEY, licenseId],
      });
      void queryClient.invalidateQueries({
        queryKey: [LICENSE_ALLOCATIONS_QUERY_KEY, licenseId],
      });
      void queryClient.invalidateQueries({
        queryKey: [INCOMING_LICENSES_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [ALL_LICENSES_QUERY_KEY],
      });

      // Double Invalidation
      invalidateGroupLists();
      setTimeout(invalidateGroupLists, 1000);

      onClose();
    },
  });

  const activeMutation = isExternal ? externalMutation : mutation;

  const handleConfirm = () => {
    activeMutation.mutate();
  };

  return (
    <Dialog
      PaperProps={{
        sx: {
          borderRadius: "24px",
          boxShadow: "0px 0px 20px rgba(0, 0, 0, 0.1)",
          width: "488px",
          maxWidth: "488px",
          p: 4,
        },
      }}
      onClose={onClose}
      open={open}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 5,
        }}
      >
        {/* Title Section */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Info
              sx={{
                fontSize: 24,
                color: "#FF9914",
              }}
            />
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "24px",
                lineHeight: "32px",
                letterSpacing: "0.02em",
                color: theme.colors.text[5],
              }}
            >
              {t("components.removeGroupAccessDialog.title")}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "0.02em",
                color: theme.colors.text[1],
              }}
            >
              <Trans
                i18nKey="components.removeGroupAccessDialog.message"
                values={{
                  experienceName,
                  groupName: displayGroupName,
                }}
              />
            </Typography>
          </Box>
        </Box>

        {/* Buttons */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 3,
            }}
          >
            <Button
              fullWidth
              onClick={onClose}
              sx={{
                height: "40px",
                background: "linear-gradient(180deg, #288BFE 0%, #0C6FE2 100%)",
                borderRadius: "8px",
                fontWeight: 600,
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "0.02em",
                color: "#FFFFFF",
                textTransform: "none",
                "&:hover": {
                  background:
                    "linear-gradient(180deg, #1a7de8 0%, #0a5ec4 100%)",
                },
              }}
            >
              {t("components.removeGroupAccessDialog.cancel")}
            </Button>
            <Button
              disabled={activeMutation.isPending}
              fullWidth
              onClick={handleConfirm}
              sx={{
                height: "40px",
                background: "#F2F2F7",
                borderRadius: "8px",
                fontWeight: 600,
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "0.02em",
                color: "#FB453A",
                textTransform: "none",
                "&:hover": {
                  background: "#e5e5ea",
                },
              }}
            >
              {t("components.removeGroupAccessDialog.remove")}
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};
